import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { NETWORK_EXTENSIONS, URLS } from '@netfoundry-ui/shared/services';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FrontdoorExtensionAvailabilityService } from './frontdoor-extension-availability.service';

/** The FD share pages in nav order, with the extension each needs. A refused reader goes to the first one that is on. */
export const FRONTDOOR_SHARE_PAGES: { route: string; extension: string }[] = [
    { route: URLS.FD_HTTP_SHARES, extension: NETWORK_EXTENSIONS.HTTP_SHARES },
    { route: URLS.FD_TCP_SHARES, extension: NETWORK_EXTENSIONS.TCP_SHARES },
    { route: URLS.FD_IPSEC, extension: NETWORK_EXTENSIONS.IPSEC_TUNNELER },
];

/**
 * Keeps an FD route off a network without the extension in `data.requiresExtension`, rather than drawing a page whose
 * every request answers 404. The reader goes to `data.extensionMissingRedirect`, else to the first share page that is on,
 * else to Agents. The server gate is the real one, so this fails open — see {@link FrontdoorExtensionAvailabilityService.check}.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorExtensionGuard implements CanActivate {
    constructor(private availability: FrontdoorExtensionAvailabilityService, private router: Router) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        const required = route.data?.['requiresExtension'] as string | undefined;
        if (!required) {
            return of(true);
        }
        const redirect = route.data?.['extensionMissingRedirect'] as string | undefined;
        return this.availability
            .check(required)
            .pipe(map((enabled) => (enabled ? true : this.router.createUrlTree([redirect ?? this.fallback()]))));
    }

    /** Read after `check`, which has set the answer for the selected network. */
    private fallback(): string {
        return (
            FRONTDOOR_SHARE_PAGES.find((page) => this.availability.isEnabled(page.extension))?.route ?? URLS.FD_AGENTS
        );
    }
}
